/**
 * The study summary the centre screen opens on (2026-10-05, the user's request): the study's
 * headline numbers under the room filter. Pure so the numbers are unit tested against the
 * raw records and the generator's precomputed summaries.
 */
import type { DashboardFilters, DashboardId } from '@clinspace/shared';
import { StudyView } from './StudyView.ts';

export interface SummaryStat {
  /** Short name under the number. */
  name: string;
  value: string;
  /** The dashboard that explains this number; selecting the tile projects it. */
  dashboard: DashboardId;
  /** One line of context, for example "of 60 target". */
  sub?: string;
  /** Draws attention (safety signals, open work). */
  alert?: boolean;
}

export interface Milestone {
  name: string;
  when: string;
  done: boolean;
}

export interface StudySummaryStats {
  filtered: boolean;
  screened: number;
  enrolled: number;
  /** The study's target, or the filtered site's target when a site filter is set. */
  target: number;
  enrolledPct: number;
  sites: number;
  countries: number;
  onTreatment: number;
  completed: number;
  discontinued: number;
  screenFailed: number;
  /** On-time share of due visits (completed or missed), one decimal. */
  onTimePct: number;
  dueVisits: number;
  openQueries: number;
  saes: number;
  majorDeviations: number;
  /** Data quality and safety (one decimal), as on the Missing Data, Data Cleaning, Lab and Profiles dashboards. */
  missingFieldsPct: number;
  cleanPct: number;
  grade3Subjects: number;
  flagged: number;
  /** Site ids the summary covers (the globe marks these). */
  siteIds: string[];
  milestones: Milestone[];
  /** Weekly cumulative randomisations and the plan, scaled to the target in view. */
  curve: { weekEnding: string; actual: number; planned: number }[];
  stats: SummaryStat[];
}

/** Same rounding as the generator's `pct` (one decimal). */
function pct(part: number, whole: number): number {
  return whole ? Math.round((part / whole) * 1000) / 10 : 0;
}

export function summaryStats(view: StudyView, f: DashboardFilters = {}): StudySummaryStats {
  const file = view.file;
  const filtered = StudyView.isFiltered(f);
  const subjects = view.subjects(f);
  const ids = new Set(subjects.map((s) => s.id));
  const enrolled = subjects.filter((s) => s.randomisationDate).length;
  const perSite = file.dashboards.enrollment.perSite;
  const target = f.siteId
    ? (perSite.find((p) => p.siteId === f.siteId)?.target ?? file.study.enrollmentTarget)
    : file.study.enrollmentTarget;
  const siteIds = filtered
    ? [...new Set(subjects.map((s) => s.siteId))].sort()
    : file.sites.map((s) => s.id);
  const countries = new Set(
    file.sites.filter((s) => siteIds.includes(s.id)).map((s) => s.countryCode),
  ).size;
  const count = (status: string) => subjects.filter((s) => s.status === status).length;
  const due = view
    .visits(f)
    .filter((v) => ids.has(v.subjectId) && (v.status === 'Completed' || v.status === 'Missed'));
  const onTime = due.filter((v) => v.timing === 'On time').length;
  const openQueries = view
    .queriesFiltered(f)
    .filter((q) => q.status === 'Open' && ids.has(q.subjectId)).length;
  const saes = view.adverseEvents(f).filter((a) => a.serious && ids.has(a.subjectId)).length;
  const majorDeviations = view
    .deviations(f)
    .filter((d) => d.classification === 'Major' && ids.has(d.subjectId)).length;
  // Data quality and safety, as the generator counts them (aggregates.ts) for the records in view.
  const forms = view.forms(f).filter((x) => ids.has(x.subjectId));
  const expectedFields = forms.reduce((sum, x) => sum + x.totalFields, 0);
  const missingFields = forms.reduce(
    (sum, x) => sum + (x.status === 'Not Started' ? x.totalFields : x.missingFields.length),
    0,
  );
  const cleanForms = forms.filter(
    (x) => x.status !== 'Not Started' && x.missingFields.length === 0,
  ).length;
  const grade3Subjects = new Set(
    view
      .labs(f)
      .filter((l) => l.grade >= 3 && ids.has(l.subjectId))
      .map((l) => l.subjectId),
  ).size;
  const flagged = subjects.filter((x) => x.flags.length > 0).length;
  const s: StudySummaryStats = {
    filtered,
    screened: subjects.length,
    enrolled,
    target,
    enrolledPct: pct(enrolled, target),
    sites: siteIds.length,
    countries,
    onTreatment: count('On Treatment'),
    completed: count('Completed'),
    discontinued: count('Discontinued') + count('Lost to Follow-up'),
    screenFailed: count('Screen Failed'),
    onTimePct: pct(onTime, due.length),
    dueVisits: due.length,
    openQueries,
    saes,
    majorDeviations,
    missingFieldsPct: 0,
    cleanPct: 0,
    grade3Subjects: 0,
    flagged: 0,
    siteIds,
    milestones: [],
    curve: [],
    stats: [],
  };
  // The generator's weekly curve, recounted from the records in view (identical unfiltered).
  const randomised = subjects.map((x) => x.randomisationDate).filter((d): d is string => !!d);
  const planScale = target / Math.max(1, file.study.enrollmentTarget);
  s.curve = file.dashboards.enrollment.cumulative.map((w) => ({
    weekEnding: w.weekEnding,
    actual: randomised.filter((d) => d <= w.weekEnding).length,
    planned: Math.round(w.planned * planScale * 10) / 10,
  }));
  s.milestones = milestones(view, f, s);
  s.stats = [
    {
      name: 'Active sites',
      value: String(s.sites),
      dashboard: 'Enrollment',
      sub: `${countries} countries`,
    },
    {
      name: 'Subjects enrolled',
      value: String(enrolled),
      dashboard: 'Enrollment',
      sub: `${s.screened} screened`,
    },
    {
      name: 'On treatment',
      value: String(s.onTreatment),
      dashboard: 'SubjectStatus',
      sub: `${s.completed} completed · ${s.discontinued} stopped`,
    },
    {
      name: 'Visits on time',
      value: `${Math.round(s.onTimePct)}%`,
      dashboard: 'VisitCompliance',
      sub: `of ${s.dueVisits} due visits`,
    },
    {
      name: 'Open queries',
      value: String(openQueries),
      dashboard: 'QueryManagement',
      alert: openQueries > 0,
    },
    { name: 'Serious AEs', value: String(saes), dashboard: 'SAEReview', alert: saes > 0 },
    {
      name: 'Major deviations',
      value: String(majorDeviations),
      dashboard: 'ProtocolDeviation',
      alert: majorDeviations > 0,
    },
    {
      name: 'Screen failures',
      value: String(s.screenFailed),
      dashboard: 'Enrollment',
      sub: `${Math.round(pct(s.screenFailed, s.screened))}% of screened`,
    },
    // From the entrance briefing, retired on 2026-10-07.
    {
      name: 'Missing fields',
      value: `${Math.round(pct(missingFields, expectedFields))}%`,
      dashboard: 'MissingData',
      sub: `${forms.filter((x) => x.overdue).length} overdue forms`,
    },
    {
      name: 'Clean forms',
      value: `${Math.round(pct(cleanForms, forms.length))}%`,
      dashboard: 'DataCleaning',
      sub: `of ${forms.length} forms`,
    },
    {
      name: 'Grade 3+ labs',
      value: String(grade3Subjects),
      dashboard: 'LabAbnormality',
      sub: 'subjects',
      alert: grade3Subjects > 0,
    },
    {
      name: 'Flagged subjects',
      value: String(flagged),
      dashboard: 'PatientProfile',
      sub: `of ${s.screened} in view`,
    },
  ];
  s.missingFieldsPct = pct(missingFields, expectedFields);
  s.cleanPct = pct(cleanForms, forms.length);
  s.grade3Subjects = grade3Subjects;
  s.flagged = flagged;
  return s;
}

/**
 * Milestones from the data: first consent, the randomisations that reached half and full
 * target, and the data cut. They follow the same subjects as the numbers.
 */
function milestones(view: StudyView, f: DashboardFilters, s: StudySummaryStats): Milestone[] {
  const file = view.file;
  const subjects = view.subjects(f);
  const consents = subjects.map((x) => x.consentDate).sort();
  const randomised = subjects
    .map((x) => x.randomisationDate)
    .filter((d): d is string => !!d)
    .sort();
  const reached = (n: number) => (n > 0 && randomised.length >= n ? randomised[n - 1] : undefined);
  const half = Math.ceil(s.target / 2);
  const halfAt = reached(half);
  const fullAt = reached(s.target);
  return [
    { name: 'First subject consented', when: consents[0] ?? '–', done: consents.length > 0 },
    {
      name: '50% enrolment',
      when: halfAt ?? `${Math.max(0, half - s.enrolled)} to go`,
      done: !!halfAt,
    },
    {
      name: 'Target enrolment',
      when: fullAt ?? `${Math.max(0, s.target - s.enrolled)} to go`,
      done: !!fullAt,
    },
    { name: 'Data cut', when: file.study.dataCutDate, done: true },
  ];
}
